import { useRef, useState } from 'react';
import {
  DEFAULT_ANNO_TEXT_SIZE,
  annoColorHex,
  annoStrokePx,
  annoTransform,
  annotationMarkersFor,
  arrowMarker,
  dimensionMarker,
  dimensionTicks,
  type AnnoColor,
  type Annotation,
} from '@jobos/shared';

import {
  clamp01,
  createArrow,
  createCircle,
  createDimension,
  createLine,
  createRect,
  createText,
  resizeBoxAnnotation,
  rotationFromPointer,
  setAnnotationRotation,
  translateAnnotation,
} from '@/lib/inspectionAnnotations';

export type AnnotationTool = 'select' | 'arrow' | 'line' | 'dimension' | 'circle' | 'rect' | 'text';

/** Tools drawn by dragging out a bounding box. They share the draft preview,
 *  the bounding box and the corner-anchored resize; only the shape drawn in the
 *  box differs (an ellipse vs. a rectangle). */
type BoxTool = 'circle' | 'rect';

/** Tools drawn as a segment between two dragged points. They share all of the
 *  geometry below (bounding box, move, resize) and differ only in end decoration. */
type SegmentTool = 'arrow' | 'line' | 'dimension';

type SegmentAnnotation = Extract<Annotation, { type: SegmentTool }>;
const isSegment = (a: Annotation): a is SegmentAnnotation =>
  a.type === 'arrow' || a.type === 'line' || a.type === 'dimension';

interface Props {
  annotations: Annotation[];
  tool: AnnotationTool;
  selectedId: string | null;
  onChange: (annotations: Annotation[]) => void;
  onSelect: (id: string | null) => void;
  /** When false the overlay is display-only (other slots) and ignores pointers. */
  interactive?: boolean;
  /** Root <svg> class. Defaults to the per-slot overlay class; the page-level
   *  overlay passes 'ins-page-anno-svg'. */
  svgClassName?: string;
  /** Sizes (viewBox units) newly drawn shapes are created with — the inspector
   *  keeps these on the last value the user picked. Omitted => render defaults. */
  newTextSize?: number;
  newArrowHeadSize?: number;
  /** Color newly drawn shapes are created with, kept the same way. */
  newColor?: AnnoColor;
}

type Point = { x: number; y: number };

const NO_POINTER = { pointerEvents: 'none' as const };

/** Just the shape of a segment — what both a stored annotation and the shape
 *  being dragged out have in common. */
type SegmentShape = {
  type: SegmentTool;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /** Arrow/dimension head size. */
  headSize?: number | undefined;
  /** A plain line's stroke weight, on the same scale. */
  size?: number | undefined;
  /** Palette key; omitted draws in the default color. */
  color?: string | undefined;
};

/** Build the annotation a drag with the given tool produces. `size` is the one
 *  the inspector last picked — a head size, or a plain line's stroke weight. */
function drawShape(kind: SegmentTool | BoxTool, start: Point, end: Point, headSize?: number, color?: AnnoColor): Annotation {
  switch (kind) {
    case 'arrow':
      return createArrow(start.x, start.y, end.x, end.y, headSize, color);
    case 'line':
      return createLine(start.x, start.y, end.x, end.y, headSize, color);
    case 'dimension':
      return createDimension(start.x, start.y, end.x, end.y, headSize, color);
    case 'circle':
      return createCircle(start.x, start.y, end.x, end.y, color);
    case 'rect':
      return createRect(start.x, start.y, end.x, end.y, color);
  }
}

/** The end marker a segment type wears: the arrow's solid head, or the
 *  dimension's open one. */
const segmentMarker = (type: 'arrow' | 'dimension', headSize?: number, color?: string) =>
  type === 'arrow' ? arrowMarker(headSize, color) : dimensionMarker(headSize, color);

// The visible part of a segment annotation: the line, plus whatever its type
// wears on the ends. Heads are <marker>s (the marker is declared
// orient="auto-start-reverse", so the one at the tail points outward too);
// the dimension's tick caps are plain lines built from the shared geometry, so
// they inherit the same stroke as everything else in .ins-anno.
function segmentShape(a: SegmentShape) {
  const headId = a.type === 'line' ? null : segmentMarker(a.type, a.headSize, a.color).id;
  const head = headId ? `url(#${headId})` : undefined;
  // Size scales the stroke on the types that offer it — the dimension's whole
  // shape, the plain line's weight. Inline, because a presentation attribute
  // would lose to .ins-anno's rule. An arrow keeps the default weight.
  const sized = a.type === 'dimension' ? a.headSize : a.type === 'line' ? a.size : undefined;
  const stroke = {
    stroke: annoColorHex(a.color),
    ...(a.type === 'arrow' ? {} : { strokeWidth: `${annoStrokePx(sized)}px` }),
  };
  return (
    <>
      <line
        className="ins-anno"
        x1={a.x1 * 100}
        y1={a.y1 * 100}
        x2={a.x2 * 100}
        y2={a.y2 * 100}
        markerStart={a.type === 'dimension' ? head : undefined}
        markerEnd={head}
        style={{ ...NO_POINTER, ...stroke }}
      />
      {a.type === 'dimension' &&
        dimensionTicks(a).map((t, i) => (
          <line key={i} className="ins-anno" x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2} style={{ ...NO_POINTER, ...stroke }} />
        ))}
    </>
  );
}

// SVG overlay for drawing/selecting/moving annotations. Reused for two overlays:
// a per-slot overlay (stretched over one photo slot) and a page-level overlay
// (stretched over the whole page) — it simply fills whatever box it's mounted in,
// and coords are normalized 0..1 of that box. viewBox 0 0 100 100 +
// preserveAspectRatio="none" mirrors the PDF, and normalized coords map linearly to it.
export function AnnotationLayer({ annotations, tool, selectedId, onChange, onSelect, interactive = true, svgClassName, newTextSize, newArrowHeadSize, newColor }: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [draft, setDraft] = useState<{ start: Point; end: Point; kind: SegmentTool | BoxTool } | null>(null);

  const toNorm = (clientX: number, clientY: number): Point => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const m = svg.getScreenCTM();
    if (!m) return { x: 0, y: 0 };
    const p = pt.matrixTransform(m.inverse());
    return { x: p.x / 100, y: p.y / 100 };
  };

  // Draw new shapes (arrow/circle drag, text click). Only fires when a drawing
  // tool is active (in select mode the svg is pointer-events:none).
  const onSvgMouseDown = (e: React.MouseEvent) => {
    if (!interactive || tool === 'select') return;
    e.preventDefault();
    e.stopPropagation(); // don't also trigger the photo-layer pan
    const start = toNorm(e.clientX, e.clientY);
    if (tool === 'text') {
      const text = window.prompt('Annotation text:')?.trim();
      if (text) {
        const a = createText(start.x, start.y, text, newTextSize, newColor);
        onChange([...annotations, a]);
        onSelect(a.id);
      }
      return;
    }
    setDraft({ start, end: start, kind: tool });
    const move = (ev: MouseEvent) => {
      const end = toNorm(ev.clientX, ev.clientY);
      setDraft((d) => (d ? { ...d, end } : d));
    };
    const up = (ev: MouseEvent) => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      const end = toNorm(ev.clientX, ev.clientY);
      setDraft(null);
      const a = drawShape(tool, start, end, newArrowHeadSize, newColor);
      onChange([...annotations, a]);
      onSelect(a.id);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  // Select + drag-move an existing shape.
  const onShapeMouseDown = (e: React.MouseEvent, a: Annotation) => {
    if (!interactive) return;
    e.stopPropagation();
    e.preventDefault(); // don't start a browser text/drag selection while moving
    onSelect(a.id);
    if (tool !== 'select') return;
    const startP = toNorm(e.clientX, e.clientY);
    const move = (ev: MouseEvent) => {
      const p = toNorm(ev.clientX, ev.clientY);
      const moved = translateAnnotation(a, p.x - startP.x, p.y - startP.y);
      onChange(annotations.map((x) => (x.id === a.id ? moved : x)));
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  // Normalized bounding box of an annotation (for the selection outline).
  const bbox = (a: Annotation): { x: number; y: number; w: number; h: number } => {
    if (isSegment(a)) {
      return { x: Math.min(a.x1, a.x2), y: Math.min(a.y1, a.y2), w: Math.abs(a.x2 - a.x1), h: Math.abs(a.y2 - a.y1) };
    }
    if (a.type === 'circle') {
      return { x: a.x - a.rx, y: a.y - a.ry, w: a.rx * 2, h: a.ry * 2 };
    }
    if (a.type === 'rect') {
      return { x: a.x, y: a.y, w: a.w, h: a.h };
    }
    // text: approximate from length × font size (baseline at y, grows up/right).
    // Font size is stored in viewBox units, this box is in normalized 0..1 space.
    const fs = (a.size ?? DEFAULT_ANNO_TEXT_SIZE) / 100;
    return { x: a.x, y: a.y - fs, w: Math.max(0.04, a.text.length * fs * 0.6), h: fs * 1.3 };
  };

  // Padding of the selection box beyond the annotation's bounding box.
  const pad = 0.012;
  // Length of the stalk the rotate grip sits on, in viewBox units — clear of
  // the selection box without drifting off a shape drawn near the top edge.
  const ROTATE_STALK = 3;

  // Blue dashed outline around the selected annotation's bounding box. In select
  // mode it's also a transparent drag handle, so dragging anywhere inside the box
  // moves the whole annotation (not just the thin shape).
  const selectionBox = (a: Annotation) => {
    const b = bbox(a);
    const movable = interactive && tool === 'select';
    return (
      <rect
        className="ins-anno-selection"
        x={(b.x - pad) * 100}
        y={(b.y - pad) * 100}
        width={(b.w + pad * 2) * 100}
        height={(b.h + pad * 2) * 100}
        style={{ fill: 'transparent', pointerEvents: movable ? 'auto' : 'none', cursor: movable ? 'move' : 'default' }}
        onMouseDown={movable ? (e) => onShapeMouseDown(e, a) : undefined}
      />
    );
  };

  // Drag a resize handle to reshape an arrow (move its head) or circle (resize
  // the ellipse, anchoring the corner opposite the handle) — like re-drawing it.
  const startResize = (e: React.MouseEvent, a: Annotation) => {
    if (!interactive || tool !== 'select') return;
    e.stopPropagation();
    e.preventDefault();
    const move = (ev: MouseEvent) => {
      const p = toNorm(ev.clientX, ev.clientY);
      let updated: Annotation;
      if (isSegment(a)) {
        updated = { ...a, x2: clamp01(p.x), y2: clamp01(p.y) };
      } else if (a.type === 'rect' || a.type === 'circle') {
        // Anchored at the corner opposite the grip — which for a tilted shape
        // means where that corner actually sits on the page, so the box grows
        // along its own axes instead of walking across the photo. Dragging past
        // the anchor flips the box rather than inverting it.
        updated = resizeBoxAnnotation(a, p);
      } else {
        return;
      }
      onChange(annotations.map((x) => (x.id === a.id ? updated : x)));
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  // Resize grip centered on the selection box's bottom-right corner.
  const resizeHandle = (a: Annotation) => {
    const b = bbox(a);
    return (
      <circle
        className="ins-anno-resize"
        cx={(b.x + b.w + pad) * 100}
        cy={(b.y + b.h + pad) * 100}
        r={0.65}
        style={{ pointerEvents: interactive && tool === 'select' ? 'all' : 'none', cursor: 'nwse-resize' }}
        onMouseDown={(e) => startResize(e, a)}
      />
    );
  };

  // Drag the rotate grip to tilt a box-drawn shape about its own center. Hold
  // Shift to land on a 15 degree stop, which is how an exact 45 is reached.
  const startRotate = (e: React.MouseEvent, a: Annotation) => {
    if (!interactive || tool !== 'select') return;
    e.stopPropagation();
    e.preventDefault();
    const move = (ev: MouseEvent) => {
      const p = toNorm(ev.clientX, ev.clientY);
      const rotated = setAnnotationRotation(annotations, a.id, rotationFromPointer(a, p, ev.shiftKey));
      onChange(rotated);
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  // Rotate grip, on a short stalk above the selection box's top edge — the
  // handle convention every drawing tool uses. Drawn inside the rotated group
  // with the rest of the chrome, so it stays welded to the shape's top.
  const rotateHandle = (a: Annotation) => {
    const b = bbox(a);
    const cx = (b.x + b.w / 2) * 100;
    const top = (b.y - pad) * 100;
    const cy = top - ROTATE_STALK;
    const grabbable = interactive && tool === 'select';
    return (
      <>
        <line className="ins-anno-rotate-stalk" x1={cx} y1={top} x2={cx} y2={cy} style={NO_POINTER} />
        <circle
          className="ins-anno-rotate"
          cx={cx}
          cy={cy}
          r={0.65}
          style={{ pointerEvents: grabbable ? 'all' : 'none', cursor: 'grab' }}
          onMouseDown={(e) => startRotate(e, a)}
        />
      </>
    );
  };

  // One <marker> per distinct kind + head size + color in use (plus the one the
  // shape being dragged needs). Ids are derived from all three, so a repeated id
  // always means an identical head — which is what makes duplicate ids across
  // overlays harmless.
  const draftMarker =
    draft && (draft.kind === 'arrow' || draft.kind === 'dimension')
      ? segmentMarker(draft.kind, newArrowHeadSize, newColor)
      : null;
  const markers = annotationMarkersFor(annotations);
  if (draftMarker && !markers.some((m) => m.id === draftMarker.id)) markers.push(draftMarker);

  const svgPointer = !interactive || tool === 'select' ? 'none' : 'auto';
  // Fat, invisible hit shapes make thin annotations easy to grab. Active only in
  // select mode (in draw mode the svg captures pointers for drawing).
  const HIT = 12; // px (non-scaling) hit-band width
  const hitStyle = {
    pointerEvents: (interactive && tool === 'select' ? 'all' : 'none') as 'all' | 'none',
    cursor: 'move' as const,
  };

  return (
    <svg
      ref={svgRef}
      className={svgClassName ?? 'ins-anno-svg'}
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{ pointerEvents: svgPointer, cursor: tool === 'select' ? 'default' : 'crosshair' }}
      onMouseDown={onSvgMouseDown}
    >
      <defs>
        {markers.map((m) => (
          <marker key={m.id} id={m.id} markerUnits="userSpaceOnUse" markerWidth={m.size} markerHeight={m.size} refX={m.refX} refY={m.refY} orient="auto-start-reverse">
            {/* Open heads carry .ins-anno, so their two strokes match the line
                they cap (same non-scaling width); both kinds paint in the
                marker's own color, since a marker can't inherit one. */}
            {m.filled ? (
              <path d={m.path} fill={m.color} />
            ) : (
              <path className="ins-anno" d={m.path} style={{ stroke: m.color, strokeWidth: `${annoStrokePx(m.size)}px` }} />
            )}
          </marker>
        ))}
      </defs>
      {annotations.map((a) => {
        const sel = a.id === selectedId;
        const onDown = (e: React.MouseEvent) => onShapeMouseDown(e, a);
        if (isSegment(a)) {
          return (
            <g key={a.id}>
              {segmentShape(a)}
              <line x1={a.x1 * 100} y1={a.y1 * 100} x2={a.x2 * 100} y2={a.y2 * 100} stroke="transparent" strokeWidth={HIT} vectorEffect="non-scaling-stroke" style={hitStyle} onMouseDown={onDown} />
              {sel && selectionBox(a)}
              {sel && resizeHandle(a)}
            </g>
          );
        }
        if (a.type === 'rect') {
          // The tilt is one transform on a group: the shape, its hit target and
          // the selection chrome all turn together, so the grips stay on the
          // corners they belong to. An untilted shape gets no transform at all.
          return (
            <g key={a.id} transform={annoTransform(a)}>
              <rect className="ins-anno" x={a.x * 100} y={a.y * 100} width={a.w * 100} height={a.h * 100} fill="none" style={{ ...NO_POINTER, stroke: annoColorHex(a.color) }} />
              <rect x={a.x * 100} y={a.y * 100} width={a.w * 100} height={a.h * 100} fill="transparent" stroke="transparent" strokeWidth={HIT} vectorEffect="non-scaling-stroke" style={hitStyle} onMouseDown={onDown} />
              {sel && selectionBox(a)}
              {sel && resizeHandle(a)}
              {sel && rotateHandle(a)}
            </g>
          );
        }
        if (a.type === 'circle') {
          return (
            <g key={a.id} transform={annoTransform(a)}>
              <ellipse className="ins-anno" cx={a.x * 100} cy={a.y * 100} rx={a.rx * 100} ry={a.ry * 100} fill="none" style={{ ...NO_POINTER, stroke: annoColorHex(a.color) }} />
              <ellipse cx={a.x * 100} cy={a.y * 100} rx={a.rx * 100} ry={a.ry * 100} fill="transparent" stroke="transparent" strokeWidth={HIT} vectorEffect="non-scaling-stroke" style={hitStyle} onMouseDown={onDown} />
              {sel && selectionBox(a)}
              {sel && resizeHandle(a)}
              {sel && rotateHandle(a)}
            </g>
          );
        }
        const tb = bbox(a);
        return (
          <g key={a.id}>
            {/* Font size and color are set inline (not just left to the CSS
                defaults) so a stored value renders here exactly as the PDF
                renders it. */}
            <text className="ins-anno-text" x={a.x * 100} y={a.y * 100} style={{ ...NO_POINTER, fontSize: a.size ?? DEFAULT_ANNO_TEXT_SIZE, fill: annoColorHex(a.color) }}>
              {a.text}
            </text>
            <rect x={(tb.x - 0.012) * 100} y={(tb.y - 0.012) * 100} width={(tb.w + 0.024) * 100} height={(tb.h + 0.024) * 100} fill="transparent" style={hitStyle} onMouseDown={onDown} />
            {sel && selectionBox(a)}
          </g>
        );
      })}
      {draft && draft.kind !== 'circle' && draft.kind !== 'rect' && (
        <g style={{ opacity: 0.7 }}>
          {segmentShape({
            type: draft.kind,
            x1: draft.start.x,
            y1: draft.start.y,
            x2: draft.end.x,
            y2: draft.end.y,
            headSize: newArrowHeadSize,
            size: newArrowHeadSize,
            color: newColor,
          })}
        </g>
      )}
      {draft && draft.kind === 'rect' && (
        <rect
          className="ins-anno"
          x={Math.min(draft.start.x, draft.end.x) * 100}
          y={Math.min(draft.start.y, draft.end.y) * 100}
          width={Math.abs(draft.end.x - draft.start.x) * 100}
          height={Math.abs(draft.end.y - draft.start.y) * 100}
          fill="none"
          style={{ opacity: 0.7, stroke: annoColorHex(newColor) }}
        />
      )}
      {draft && draft.kind === 'circle' && (
        <ellipse
          className="ins-anno"
          cx={((draft.start.x + draft.end.x) / 2) * 100}
          cy={((draft.start.y + draft.end.y) / 2) * 100}
          rx={(Math.abs(draft.end.x - draft.start.x) / 2) * 100}
          ry={(Math.abs(draft.end.y - draft.start.y) / 2) * 100}
          fill="none"
          style={{ opacity: 0.7, stroke: annoColorHex(newColor) }}
        />
      )}
    </svg>
  );
}
